const API_SCRIPT_ATTR = 'data-recaptcha-api';
const API_TAG_EVENT = 'tronshop:recaptcha-api';
const TOKEN_FIELD_SELECTOR = 'input[name="RecaptchaToken"][data-recaptcha-sitekey]';

const TOKEN_TIMEOUT_MS = 8000;
// How long a submit waits for the page's own api.js tag before adding one itself.
const PAGE_TAG_WAIT_MS = 4000;

declare const grecaptcha: any;

// Never let a form wait forever on Google (blocked script, failed tag, ready() that never fires): after the timeout
// the form is sent without a token and the server answers with its normal reCAPTCHA result.
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
    return Promise.race([promise, new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), ms))]);
}

// The load this module is waiting for. A failed one is cleared, so the next submit tries again.
let apiLoad: Promise<void> | undefined;

// Resolves once the api.js tag rendered by _RecaptchaV3.cshtml has loaded or failed. That tag is async, so a submit can
// come before it finishes; adding a second tag then would load the API twice. Its inline handlers keep
// window.TronShop.recaptchaApi up to date and fire API_TAG_EVENT on window ('loading' is set only when the parser
// runs the partial; a copy that comes in through innerHTML never loads and never sets it). A tag still loading after
// PAGE_TAG_WAIT_MS counts as failed: a second api.js load on a slow network is accepted then, a form waiting on it is not.
function waitForPageApiTag(): Promise<void> {
    return new Promise(resolve => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const done = () => {
            clearTimeout(timer);
            window.removeEventListener(API_TAG_EVENT, settle);
            resolve();
        };
        const settle = () => {
            if (window.TronShop?.recaptchaApi !== 'loading') {
                done();
            }
        };
        window.addEventListener(API_TAG_EVENT, settle);
        timer = setTimeout(done, PAGE_TAG_WAIT_MS);
        settle();
    });
}

// `load` is the load this tag belongs to; a failed tag clears only that one, a later submit may have started another.
function addApiTag(siteKey: string, load: Promise<void>): Promise<void> {
    return new Promise(resolve => {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.setAttribute(API_SCRIPT_ATTR, '');
        script.addEventListener('load', () => resolve(), { once: true });
        script.addEventListener('error', () => {
            script.remove();
            if (apiLoad === load) {
                apiLoad = undefined;
            }
            resolve();
        }, { once: true });
        document.head.appendChild(script);
    });
}

function loadApi(siteKey: string): Promise<void> {
    if (typeof grecaptcha !== 'undefined') {
        return Promise.resolve();
    }
    if (apiLoad) {
        return apiLoad;
    }

    // Our own tag only when the page's tag failed or there is none that runs (form loaded through innerHTML).
    const load: Promise<void> = waitForPageApiTag().then(() => typeof grecaptcha === 'undefined' ? addApiTag(siteKey, load) : undefined);
    apiLoad = load;
    return load;
}

/**
 * Fills the form's hidden RecaptchaToken field with a fresh reCAPTCHA v3 token.
 * Must run right before every submit: tokens are single-use and expire after 2 minutes,
 * and forms are re-rendered via innerHTML on validation errors (inline scripts would not run).
 * Does nothing when the form has no reCAPTCHA field (reCAPTCHA disabled for the shop).
 */
export async function ensureRecaptchaToken(form: HTMLFormElement): Promise<void> {
    const tokenField = form.querySelector<HTMLInputElement>(TOKEN_FIELD_SELECTOR);
    if (!tokenField) {
        return;
    }

    const siteKey = tokenField.dataset.recaptchaSitekey;
    const action = tokenField.dataset.recaptchaAction;

    // One deadline for loading the API and getting the token together.
    const deadline = Date.now() + TOKEN_TIMEOUT_MS;
    try {
        await withTimeout(loadApi(siteKey), TOKEN_TIMEOUT_MS);
        if (typeof grecaptcha === 'undefined') {
            // Gave up on this load; the next submit starts a new one.
            apiLoad = undefined;
            return;
        }

        tokenField.value = (await withTimeout(new Promise<string>((resolve, reject) =>
            grecaptcha.ready(() => grecaptcha.execute(siteKey, { action }).then(resolve, reject))), Math.max(0, deadline - Date.now()))) ?? '';
    } catch (e) {
        // Leave the field empty; the server decides (fail-open on transport problems, fail-closed on validation).
        console.error('reCAPTCHA token could not be obtained', e);
    }
}
